/** Libellés macro cases.current_phase (affichage dentiste, sans détail phases masquées). */
export const CASE_CURRENT_PHASE_LABELS = {
  submission: 'Soumission de la Conception',
  review: 'Révision Initiale',
  design: 'Phase de Conception',
  production: 'Production',
  quality_check: 'Contrôle Qualité',
  shipping: 'Expédition',
  delivered: 'Livré',
};

const PHASE_DONE_STATUSES = new Set(['completed', 'submitted']);

/** Lot 1 : 9 phases (soumission → expédition). Lots suivants : 6 phases. */
export function getShippingPhaseOrderForGroup(groupNum) {
  return groupNum === 1 ? 9 : 6;
}

/** Supabase renvoie parfois un objet unique au lieu d’un tableau (relation 1-N). */
export function normalizeCasePhasesList(phases) {
  if (!phases) return [];
  if (Array.isArray(phases)) return phases;
  return [phases];
}

export function normalizeMilestonesList(milestones) {
  if (!milestones) return [];
  if (Array.isArray(milestones)) return milestones;
  return [milestones];
}

/** Phases + jalons normalisés pour filtres liste / hub dentiste. */
export function normalizeCasePhasesForList(phases) {
  return normalizeCasePhasesList(phases).map((phase) => ({
    ...phase,
    milestones: normalizeMilestonesList(phase.milestones),
  }));
}

function isPhaseCompleted(phase) {
  return PHASE_DONE_STATUSES.has(phase?.status);
}

/** Numéros de lots présents sur le dossier, triés croissant. */
export function getCaseGroupNumbers(phases) {
  const groups = new Set();
  for (const phase of normalizeCasePhasesList(phases)) {
    const g = parseInt(String(phase?.group ?? ''), 10);
    if (!Number.isNaN(g)) groups.add(g);
  }
  return [...groups].sort((a, b) => a - b);
}

function computeGroupWorkflowProgress(phases, groupNum) {
  const shippingOrder = getShippingPhaseOrderForGroup(groupNum);
  const groupKey = String(groupNum);

  const scopedPhases = normalizeCasePhasesList(phases).filter(
    (p) =>
      String(p?.group ?? '') === groupKey &&
      p?.status !== 'cancelled' &&
      (p.phase_order ?? 0) >= 1 &&
      (p.phase_order ?? 0) <= shippingOrder
  );

  const totalUnits = scopedPhases.length;
  const completedUnits = scopedPhases.filter(isPhaseCompleted).length;
  const percent =
    totalUnits === 0
      ? 0
      : Math.min(100, Math.max(0, Math.round((completedUnits / totalUnits) * 100)));
  const hasStarted = scopedPhases.some((p) => p.status !== 'pending');

  return { percent, completedUnits, totalUnits, hasStarted };
}

function buildLotsProgressSummary(phases, activeGroupNum) {
  return getCaseGroupNumbers(phases).map((groupNum) => {
    const groupProgress = computeGroupWorkflowProgress(phases, groupNum);
    let status = 'pending';
    if (groupNum === activeGroupNum) {
      status = groupProgress.percent >= 100 ? 'completed' : 'active';
    } else if (groupProgress.percent >= 100) {
      status = 'completed';
    } else if (groupProgress.hasStarted) {
      status = 'in_progress';
    }
    return { groupNum, status, ...groupProgress };
  });
}

/**
 * Groupe actif = le plus élevé ayant au moins une phase non pending.
 * @returns {number} 1 par défaut si aucun groupe activé
 */
export function getActiveCaseGroupNum(phases) {
  const groupMap = {};
  for (const phase of normalizeCasePhasesList(phases)) {
    const g = String(phase?.group ?? '');
    if (!g) continue;
    if (!groupMap[g]) groupMap[g] = [];
    groupMap[g].push(phase);
  }

  const activated = Object.entries(groupMap)
    .filter(([, list]) => list.some((p) => p.status !== 'pending'))
    .map(([g]) => parseInt(g, 10))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => b - a);

  return activated[0] ?? 1;
}

/**
 * Progression du lot en cours : phases terminées / total (1ère → expédition).
 * @param {Array<{ status?: string, group?: string|number, phase_order?: number }>} phases
 * @param {string|null} currentPhase — cases.current_phase pour libellé macro
 */
export function computeCaseWorkflowProgress(phases, currentPhase = null) {
  const activeGroupNum = getActiveCaseGroupNum(phases);
  const { percent, completedUnits, totalUnits } = computeGroupWorkflowProgress(
    phases,
    activeGroupNum
  );
  const lots = buildLotsProgressSummary(phases, activeGroupNum);

  const labelKey = currentPhase || null;
  const macroLabel =
    (labelKey && CASE_CURRENT_PHASE_LABELS[labelKey]) || null;

  return {
    percent,
    completedUnits,
    totalUnits,
    activeGroupNum,
    totalLots: lots.length,
    lots,
    labelKey,
    macroLabel,
  };
}

export function getCaseWorkflowMacroLabel(currentPhase) {
  if (!currentPhase) return null;
  return CASE_CURRENT_PHASE_LABELS[currentPhase] || null;
}
